"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  uploadDocumentsRequest,
  updateKycFieldsRequest,
} from "@/store/slices/kyc.slice";
import { formDataUtils } from "@/services/kycService";
import { getUpdatableFieldsFromProblems } from "@/types/models/kyc";
import { store } from "@/store/store";
import FileUpload from "./FileUpload";
import KYCErrorDisplay from "./KYCErrorDisplay";
import { useFieldHasErrors } from "./KYCFieldError";

// Validation Schema
const documentUploadSchema = z.object({
  aadhaar_front_file: z
    .any()
    .optional()
    .refine(
      (file) => !file || file instanceof File,
      "Aadhaar front document must be a valid file",
    ),
  aadhaar_back_file: z
    .any()
    .optional()
    .refine(
      (file) => !file || file instanceof File,
      "Aadhaar back document must be a valid file",
    ),
  pan_file: z
    .any()
    .optional()
    .refine(
      (file) => !file || file instanceof File,
      "PAN document must be a valid file",
    ),
});

type DocumentUploadFormData = {
  aadhaar_front_file?: File;
  aadhaar_back_file?: File;
  pan_file?: File;
};

interface DocumentUploadStepProps {
  onPrevious: () => void;
  storeId: string;
  kycData?: any | null;
}

export const DocumentUploadStep: React.FC<DocumentUploadStepProps> = ({
  onPrevious,
  storeId,
  kycData = null,
}) => {
  const dispatch = useAppDispatch();
  const {
    isLoading,
    error,
    isResubmissionMode,
    rejectedSteps,
    kycProblems,
    rejectionReason,
    kycStatus,
  } = useAppSelector((state) => state.kyc);

  // Check if document fields have errors
  const aadhaarFrontHasError = useFieldHasErrors(
    "aadhaar_document_front",
    kycProblems,
  );
  const aadhaarBackHasError = useFieldHasErrors(
    "aadhaar_document_back",
    kycProblems,
  );
  const panHasError = useFieldHasErrors("pan_document", kycProblems);

  const [aadhaarFrontFile, setAadhaarFrontFile] = useState<
    File | string | null
  >(kycData?.aadhaar_document_front || null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState<File | string | null>(
    kycData?.aadhaar_document_back || null,
  );
  const [panFile, setPanFile] = useState<File | string | null>(
    kycData?.pan_document || null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Track if form has been submitted to prevent resetting user input on errors
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<DocumentUploadFormData>({
    resolver: zodResolver(documentUploadSchema),
  });

  // Update form values when files change
  React.useEffect(() => {
    if (aadhaarFrontFile)
      setValue("aadhaar_front_file", aadhaarFrontFile as any);
    if (aadhaarBackFile) setValue("aadhaar_back_file", aadhaarBackFile as any);
    if (panFile) setValue("pan_file", panFile as any);
  }, [aadhaarFrontFile, aadhaarBackFile, panFile, setValue]);

  // Initialize files from kycData only on first load, not after user interactions
  React.useEffect(() => {
    if (kycData && !hasSubmitted) {
      if (kycData.aadhaar_document_front && !aadhaarFrontFile) {
        setAadhaarFrontFile(kycData.aadhaar_document_front);
      }
      if (kycData.aadhaar_document_back && !aadhaarBackFile) {
        setAadhaarBackFile(kycData.aadhaar_document_back);
      }
      if (kycData.pan_document && !panFile) {
        setPanFile(kycData.pan_document);
      }
    }
  }, [kycData, hasSubmitted, aadhaarFrontFile, aadhaarBackFile, panFile]);

  const onSubmit = async () => {
    setSubmitError(null);

    // Clear any stale Redux errors before starting
    dispatch({ type: "kyc/clearError" });
    
    // Mark that user has attempted submission to prevent form resets
    setHasSubmitted(true);

    // For resubmissions, only validate files that are actually problematic
    if (isResubmissionMode && rejectedSteps?.documentUpload) {
      const updatableFields = getUpdatableFieldsFromProblems(kycProblems || []);
      const missingFiles = [];

      if (
        updatableFields.includes("aadhaar_document_front") &&
        !aadhaarFrontFile
      ) {
        missingFiles.push("Aadhaar Card (Front)");
      }
      if (
        updatableFields.includes("aadhaar_document_back") &&
        !aadhaarBackFile
      ) {
        missingFiles.push("Aadhaar Card (Back)");
      }
      if (updatableFields.includes("pan_document") && !panFile) {
        missingFiles.push("PAN Card");
      }

      if (missingFiles.length > 0) {
        setSubmitError(
          `Please upload the following problematic documents: ${missingFiles.join(", ")}`,
        );
        return;
      }
    } else {
      // For initial submissions, require all documents
      if (!aadhaarFrontFile || !aadhaarBackFile || !panFile) {
        setSubmitError("Please upload all required documents");
        return;
      }
    }

    try {
      // Use PATCH endpoint for resubmissions, POST for initial submissions
      if (isResubmissionMode && rejectedSteps?.documentUpload) {
        // PATCH: Update only the fields that were actually rejected
        const updatableFields = getUpdatableFieldsFromProblems(
          kycProblems || [],
        );
        const fields: Record<string, unknown> = {};

        // Only include document fields that were marked as problematic
        if (
          updatableFields.includes("aadhaar_document_front") &&
          aadhaarFrontFile &&
          typeof aadhaarFrontFile !== "string"
        ) {
          fields.aadhaar_document_front = aadhaarFrontFile;
        }
        if (
          updatableFields.includes("aadhaar_document_back") &&
          aadhaarBackFile &&
          typeof aadhaarBackFile !== "string"
        ) {
          fields.aadhaar_document_back = aadhaarBackFile;
        }
        if (
          updatableFields.includes("pan_document") &&
          panFile &&
          typeof panFile !== "string"
        ) {
          fields.pan_document = panFile;
        }

        // Ensure we have at least one field to update
        if (Object.keys(fields).length === 0) {
          setSubmitError(
            "No problematic documents found to update. Please contact support.",
          );
          return;
        }

        console.log(
          "[DocumentUploadStep] Updating fields:",
          Object.keys(fields),
        );
        console.log(
          "[DocumentUploadStep] Updatable fields from problems:",
          updatableFields,
        );

        await new Promise((resolve, reject) => {
          let resolved = false;

          // Add timeout to prevent hanging
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              unsubscribe();
              resolve(true); // Assume success after timeout
            }
          }, 10000); // 10 second timeout

          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            // Only check loading state changes, ignore stale errors
            if (!state.kyc.isLoading && !resolved) {
              clearTimeout(timeout);
              // Clear any stale errors since we're handling success/failure explicitly
              if (
                state.kyc.error &&
                state.kyc.error.toLowerCase().includes("update")
              ) {
                // Only reject if error is related to the current update operation
                resolved = true;
                unsubscribe();
                reject(new Error(state.kyc.error));
              } else {
                // Success case - updateKycFields completed without update-related errors
                resolved = true;
                unsubscribe();
                resolve(true);
              }
            }
          });

          dispatch(
            updateKycFieldsRequest({
              storeId,
              fields,
            }),
          );
        });
      } else {
        // POST: Initial document upload — only include fields that are File objects
        const formDataPayload: Record<string, File> = {};
        if (aadhaarFrontFile && typeof aadhaarFrontFile !== "string") {
          formDataPayload.aadhaar_document_front = aadhaarFrontFile;
        }
        if (aadhaarBackFile && typeof aadhaarBackFile !== "string") {
          formDataPayload.aadhaar_document_back = aadhaarBackFile;
        }
        if (panFile && typeof panFile !== "string") {
          formDataPayload.pan_document = panFile;
        }

        const formData = formDataUtils.createDocumentUploadFormData(
          formDataPayload as any,
        );

        await new Promise((resolve, reject) => {
          let resolved = false;

          // Add timeout to prevent hanging
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              unsubscribe();
              resolve(true); // Assume success after timeout
            }
          }, 10000); // 10 second timeout

          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (!state.kyc.isLoading && !resolved) {
              clearTimeout(timeout);
              // Check for upload-specific errors
              if (
                state.kyc.error &&
                (state.kyc.error.toLowerCase().includes("upload") ||
                  state.kyc.error.toLowerCase().includes("document"))
              ) {
                resolved = true;
                unsubscribe();
                reject(new Error(state.kyc.error));
              } else if (state.kyc.completedSteps.documentUpload) {
                // Success case - documents uploaded and step marked complete
                resolved = true;
                unsubscribe();
                resolve(true);
              } else {
                // Loading finished but step not marked complete - treat as success anyway
                // since the API returned success (checked in saga)
                resolved = true;
                unsubscribe();
                resolve(true);
              }
            }
          });

          dispatch(
            uploadDocumentsRequest({
              storeId,
              formData,
            }),
          );
        });
      }

      // Success - Redux will automatically advance to the next step
      console.log(
        "[DocumentUploadStep] Documents uploaded successfully, Redux will handle step advancement",
      );
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to upload documents. Please try again.",
      );
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Document Upload</h2>
        <p className="text-auth-text-muted">
          Please upload clear photos of your identity documents. Make sure all
          text is readable.
        </p>
      </div>

      {/* KYC Error Display */}
      {kycStatus === "REJECTED" && (kycProblems?.length || rejectionReason) && (
        <div className="mb-6">
          <KYCErrorDisplay
            problems={kycProblems}
            rejectionReason={rejectionReason}
          />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Aadhaar Front */}
        <FileUpload
          onFileSelect={setAadhaarFrontFile}
          accept="image/jpeg,image/png"
          maxSize={2 * 1024 * 1024} // 2MB
          fileType="image"
          label="Aadhaar Card (Front)"
          description="Upload front side of your Aadhaar card (JPEG/PNG, max 2MB)"
          required
          currentFile={aadhaarFrontFile}
          error={
            errors.aadhaar_front_file?.message ||
            (aadhaarFrontHasError ? "Please reupload this document" : undefined)
          }
          hasKYCError={aadhaarFrontHasError}
          isUploading={isLoading}
        />

        {/* Aadhaar Back */}
        <FileUpload
          onFileSelect={setAadhaarBackFile}
          accept="image/jpeg,image/png"
          maxSize={2 * 1024 * 1024} // 2MB
          fileType="image"
          label="Aadhaar Card (Back)"
          description="Upload back side of your Aadhaar card (JPEG/PNG, max 2MB)"
          required
          currentFile={aadhaarBackFile}
          error={
            errors.aadhaar_back_file?.message ||
            (aadhaarBackHasError ? "Please reupload this document" : undefined)
          }
          hasKYCError={aadhaarBackHasError}
          isUploading={isLoading}
        />

        {/* PAN Card */}
        <FileUpload
          onFileSelect={setPanFile}
          accept="image/jpeg,image/png"
          maxSize={2 * 1024 * 1024} // 2MB
          fileType="image"
          label="PAN Card"
          description="Upload your PAN card (JPEG/PNG, max 2MB)"
          required
          currentFile={panFile}
          error={
            errors.pan_file?.message ||
            (panHasError ? "Please reupload this document" : undefined)
          }
          hasKYCError={panHasError}
          isUploading={isLoading}
        />

        {/* Guidelines */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-2">Document Guidelines:</h3>
          <ul className="text-auth-text-muted text-sm space-y-1">
            <li>• Ensure all text and details are clearly visible</li>
            <li>• Documents should be well-lit and in focus</li>
            <li>• Avoid shadows, glare, or blurred images</li>
            <li>• File formats: JPEG or PNG only</li>
            <li>• Maximum file size: 2MB per document</li>
          </ul>
        </div>

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500 text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="flex items-center space-x-2 px-6 py-3 bg-muted/50 border border-border rounded-[30px] text-muted-foreground hover:text-foreground hover:bg-muted hover:border-muted-foreground/50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ChevronDown className="w-5 h-5 rotate-90" />
            <span>Previous</span>
          </button>

          <button
            type="submit"
            disabled={
              isLoading ||
              (isResubmissionMode && rejectedSteps?.documentUpload
                ? (() => {
                    const updatableFields = getUpdatableFieldsFromProblems(
                      kycProblems || [],
                    );
                    return (
                      (updatableFields.includes("aadhaar_document_front") &&
                        !aadhaarFrontFile) ||
                      (updatableFields.includes("aadhaar_document_back") &&
                        !aadhaarBackFile) ||
                      (updatableFields.includes("pan_document") && !panFile)
                    );
                  })()
                : !aadhaarFrontFile || !aadhaarBackFile || !panFile)
            }
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:bg-primary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl rounded-[30px] transform hover:scale-105 active:scale-95"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isLoading ? "Uploading..." : "Next"}</span>
            {!isLoading && <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DocumentUploadStep;
